import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { ScreenBlock, CTABand, SectionHead } from "@/components/site/ui";
import { CarePlansScreen } from "@/components/site/mockups";

const IMG = "/screens/supported-living.png";

export default function SupportedLiving() {
  useSEO(
    "Supported Living Software | AYOBELL Care Management",
    "AYOBELL helps supported living providers support independence, track outcomes and goals, manage medication and rota, and work across multiple houses with clear oversight."
  );
  return (
    <>
      <PageHero
        eyebrow="Supported Living"
        title="Supporting independence. Giving teams better visibility."
        subtitle="AYOBELL helps supported living services focus on outcomes and independence while giving managers clear visibility across every house."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
        screenKey="careplans"
        screenComp={CarePlansScreen}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden h-64 sm:h-80">
            <img src={IMG} alt="Care worker supporting a person in a supported living setting" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="Person-centred"
            title="Person-centred support built around the individual"
            desc="Capture who someone is and what matters to them, then plan support around their outcomes and future goals."
            points={["About Me / person overview", "Needs, outcomes & future goals", "Consent and important people", "Daily notes & observations"]}
            screenKey="serviceusers"
          />
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            reverse
            eyebrow="Medication & appointments"
            title="Medication and appointments, clearly recorded"
            desc="Keep medication records and appointments organised, with risks and alerts visible to the team."
            points={["Medication records & MAR / eMAR", "PRN medication", "Appointments", "Alerts & risks"]}
            screenKey="emar"
          />
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="Workforce & multi-house"
            title="Rota and multi-house working in one place"
            desc="Plan staff across houses and sites, and filter by location so managers see exactly what they need."
            points={["Staff rota & shift management", "Multi-house / multi-site management", "Attendance", "Site / house filtering"]}
            screenKey="rota"
          />
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead center eyebrow="Oversight" title="Governance and management oversight" desc="Bring incidents, safeguarding, supervisions and readiness indicators together to support continuous improvement." />
          <div className="mt-14">
            <ScreenBlock
              reverse
              title="A clear view for registered managers"
              desc="AYOBELL readiness indicators and governance evidence help managers stay on top of quality across the service."
              points={["Incidents & safeguarding", "Supervisions & training compliance", "Audits & action plans", "AYOBELL CQC readiness dashboard"]}
              screenKey="cqc"
              url="app.ayobell.com/governance"
            />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
